import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { CircleNotch, File } from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useState } from "react";

import { SearchProgress } from "./SearchProgress";
import { StructuredResponse } from "./StructuredResponse";

import { Markdown } from "../../../components/Markdown";
import { type StreamState } from "../hooks/useStreamSimulation";

/** Image with loading ghost - shows skeleton until image loads, reserves space */
function ImageWithLoader({
  src,
  alt,
  href,
}: {
  src: string;
  alt: string;
  href: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="relative block min-h-32 min-w-32 overflow-hidden rounded-lg">
      {!loaded && !error && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden
        >
          <div className="h-32 w-48 animate-pulse rounded-lg bg-puntt-neutral-gray-11" />
        </div>
      )}
      {error ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-24 items-center justify-center rounded-lg border border-puntt-neutral-gray-10 bg-puntt-neutral-gray-11/50 px-4 text-puntt-neutral-gray-11"
        >
          <Text size="2">Image failed to load — click to open</Text>
        </a>
      ) : (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <img
            src={src}
            alt={alt}
            className={`max-h-48 max-w-full object-contain transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        </a>
      )}
    </div>
  );
}

// Robot icon from the existing BrandCheck ChatMessage
export function RobotIcon() {
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-accent-7">
      <svg
        width="20"
        height="19"
        viewBox="0 0 30 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.4939 5.67486H15.8328V2.34435C15.8328 2.12352 15.7451 1.91174 15.589 1.75559C15.4328 1.59944 15.221 1.51172 15.0002 1.51172C14.7794 1.51172 14.5676 1.59944 14.4114 1.75559C14.2553 1.91174 14.1676 2.12352 14.1676 2.34435V5.67486H7.50654C6.62323 5.67486 5.7761 6.02576 5.15151 6.65035C4.52692 7.27494 4.17603 8.12207 4.17603 9.00538V20.6622C4.17603 21.5455 4.52692 22.3926 5.15151 23.0172C5.7761 23.6418 6.62323 23.9927 7.50654 23.9927H22.4939C23.3772 23.9927 24.2243 23.6418 24.8489 23.0172C25.4735 22.3926 25.8244 21.5455 25.8244 20.6622V9.00538C25.8244 8.12207 25.4735 7.27494 24.8489 6.65035C24.2243 6.02576 23.3772 5.67486 22.4939 5.67486Z"
          fill="#21201C"
        />
        <circle
          cx="10.428"
          cy="13.5947"
          r="2.91667"
          stroke="#fff"
          strokeWidth="1.66667"
        />
        <circle
          cx="19.5947"
          cy="13.5947"
          r="2.91667"
          stroke="#fff"
          strokeWidth="1.66667"
        />
      </svg>
    </div>
  );
}

interface ChatMessageProps {
  message: AskPunttMessage;
  /** If provided, renders with streaming animation instead of full content */
  streamState?: StreamState;
}

export function ChatMessage({ message, streamState }: ChatMessageProps) {
  if (message.role === "user") {
    return (
      <Flex justify="end" className="mb-4">
        <div className="max-w-[70%] space-y-2 rounded-2xl bg-puntt-neutral-gray-12 px-4 py-2.5">
          <Text size="2" className="text-base-white">
            {message.content}
          </Text>
          {message.attachments && message.attachments.length > 0 && (
            <Flex direction="column" gap="2" className="mt-2">
              {message.attachments.map((att, idx) => {
                const mediaUrl =
                  att.source ||
                  (att.key ? `https://static.puntt.ai/${att.key}` : "");
                return (
                  <div key={att.key ?? idx}>
                    {att.type === "image" && mediaUrl && (
                      <ImageWithLoader
                        src={mediaUrl}
                        alt={att.name ?? "Attachment"}
                        href={mediaUrl}
                      />
                    )}
                    {att.type === "video" && mediaUrl && (
                      <video
                        src={mediaUrl}
                        controls
                        className="max-h-48 max-w-full rounded-lg"
                      >
                        <track kind="captions" />
                      </video>
                    )}
                    {att.type === "file" && (mediaUrl || att.source) && (
                      <a
                        href={mediaUrl || att.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg bg-puntt-neutral-gray-11/30 px-2 py-1.5 text-base-white hover:underline"
                      >
                        <File size={16} />
                        <Text size="2">{att.name ?? "Download"}</Text>
                      </a>
                    )}
                  </div>
                );
              })}
            </Flex>
          )}
        </div>
      </Flex>
    );
  }

  // If streaming and not yet complete, render the progressive version
  if (streamState && !streamState.isComplete) {
    return (
      <StreamingAssistantMessage message={message} streamState={streamState} />
    );
  }

  // Fully rendered assistant message
  const hasContent = !!message.content;
  const hasSearchProgress =
    message.searchProgress && message.searchProgress.length > 0;
  const isPending = message.status === "pending";
  const allSearchesDone =
    hasSearchProgress &&
    message.searchProgress!.every((sp) => sp.status === "done");

  return (
    <div className="mb-4 space-y-3">
      {/* Working indicator when placeholder has no content yet */}
      {!hasContent && !hasSearchProgress && (
        <div className="flex items-center gap-2 rounded-lg bg-base-white p-4">
          <div className="flex gap-1">
            <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
            <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
            <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
          </div>
          <Text size="2" className="text-puntt-neutral-gray-9">
            Working...
          </Text>
        </div>
      )}

      {/* Ack / main content */}
      {hasContent && (
        <div className="text-puntt-neutral-gray-12">
          <Markdown>{message.content}</Markdown>
        </div>
      )}

      {/* Search progress cards */}
      {hasSearchProgress && (
        <SearchProgress searchProgress={message.searchProgress!} />
      )}

      {/* Status indicator while pending */}
      {isPending && (
        <div className="flex items-center gap-2 rounded-lg bg-base-white p-4">
          <div className="flex gap-1">
            <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
            <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
            <span className="size-2 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
          </div>
          <Text size="2" className="text-puntt-neutral-gray-9">
            {allSearchesDone
              ? "Synthesizing your answer..."
              : hasSearchProgress
                ? "Searching..."
                : hasContent
                  ? "Searching..."
                  : "Thinking..."}
          </Text>
        </div>
      )}

      {/* Structured response */}
      {message.structuredContent && (
        <StructuredResponse content={message.structuredContent} />
      )}
    </div>
  );
}

/** Renders the message progressively based on stream state */
function StreamingAssistantMessage({
  message,
  streamState,
}: {
  message: AskPunttMessage;
  streamState: StreamState;
}) {
  const {
    phase,
    searchProgress,
    streamedContent,
    showStructured,
    structuredSections,
  } = streamState;

  return (
    <div className="mb-4 space-y-3">
      {/* Thinking indicator before content starts */}
      {phase === "searching" && !streamedContent && (
        <Flex align="center" gap="2">
          <CircleNotch size={14} className="animate-spin text-puntt-accent-9" />
          <Text size="2" className="text-puntt-neutral-gray-9">
            Thinking...
          </Text>
        </Flex>
      )}

      {/* Search progress (progressive, auto-expanded during streaming) */}
      {searchProgress.length > 0 && (
        <SearchProgress
          searchProgress={searchProgress}
          streaming={phase === "searching" || phase === "content"}
        />
      )}

      {/* Streamed content (markdown) */}
      {streamedContent && (
        <div className="text-puntt-neutral-gray-12">
          <Markdown>{streamedContent}</Markdown>
          {phase === "content" && (
            <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-puntt-neutral-gray-12" />
          )}
        </div>
      )}

      {/* Structured response (progressive sections) */}
      {showStructured && message.structuredContent && (
        <StructuredResponse
          content={message.structuredContent}
          maxSections={structuredSections}
        />
      )}
    </div>
  );
}
